import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../config/supabase';
import AgentService from '../../services/agentService';

interface KnowledgeDocument {
  type: 'file' | 'url' | 'text';
  id: string;
  name: string;
  prompt_injectable: boolean;
  metadata?: {
    created_at_unix_secs: number;
    last_updated_at_unix_secs: number;
    size_bytes: number;
  };
  access_info?: {
    is_creator: boolean;
    creator_name: string;
    creator_email: string;
    role: string;
  };
  dependent_agents?: any[];
}

interface KnowledgeBaseProps {
  apiKey: string;
}

const KnowledgeBase: React.FC<KnowledgeBaseProps> = ({ apiKey }) => {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [uploadType, setUploadType] = useState<'file' | 'url' | 'text'>('text');
  const [formData, setFormData] = useState({
    name: '',
    text: '',
    url: '',
    file: null as File | null,
    promptInjectable: true,
  });
  const [uploading, setUploading] = useState(false);
  const [agents, setAgents] = useState<Array<{ agent_id: string; name: string }>>([]);
  const [docToAgentSelection, setDocToAgentSelection] = useState<Record<string, string>>({});

  const fetchDocuments = useCallback(async () => {
    if (!apiKey) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user's document IDs from our tracking table
      const { data: userDocuments, error: dbError } = await supabase
        .from('user_documents')
        .select('*');

      if (dbError) {
        console.error('Error fetching user documents from database:', dbError);
        setDocuments([]);
        return;
      }

      if (!userDocuments || userDocuments.length === 0) {
        setDocuments([]);
        return;
      }

      // Fetch the actual document details from ElevenLabs for user's documents
      const documentDetails: KnowledgeDocument[] = [];
      
      for (const userDoc of userDocuments) {
        try {
          const response = await fetch(`https://api.elevenlabs.io/v1/convai/knowledge-base/${userDoc.elevenlabs_document_id}`, {
            headers: {
              'xi-api-key': apiKey,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const docData = await response.json();
            documentDetails.push({
              ...docData,
              // Override with our stored data for consistency
              name: userDoc.name,
              type: userDoc.document_type as 'file' | 'url' | 'text'
            });
          } else {
            // Document might have been deleted from ElevenLabs, clean up our database
            console.warn(`Document ${userDoc.elevenlabs_document_id} not found in ElevenLabs, removing from database`);
            await supabase
              .from('user_documents')
              .delete()
              .eq('elevenlabs_document_id', userDoc.elevenlabs_document_id);
          }
        } catch (error) {
          console.error(`Error fetching document ${userDoc.elevenlabs_document_id}:`, error);
        }
      }
      
      setDocuments(documentDetails);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch knowledge base');
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const fetchAgents = useCallback(async () => {
    if (!apiKey) return;
    try {
      const agentService = new AgentService(apiKey);
      const userAgents = await agentService.getUserAgents();
      setAgents(userAgents.map(a => ({ agent_id: a.agent_id, name: a.name })));
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  }, [apiKey]);

  const attachDocumentToAgent = async (documentId: string) => {
    const agentId = docToAgentSelection[documentId];
    if (!agentId) {
      setError('Please select an agent to attach this document to.');
      return;
    }
    if (!agents.some(a => a.agent_id === agentId)) {
      setError('Invalid agent selection.');
      return;
    }
    try {
      const getResp = await fetch(`https://api.elevenlabs.io/v1/convai/agents/${agentId}`, {
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });
      if (!getResp.ok) {
        const t = await getResp.text().catch(() => '');
        throw new Error(`Failed to load agent: ${t || getResp.statusText}`);
      }
      const agentData = await getResp.json();
      const existingIds: string[] = Array.isArray(agentData.knowledge_base_ids)
        ? agentData.knowledge_base_ids
        : Array.isArray(agentData.knowledge_base)
          ? agentData.knowledge_base.map((d: any) => (typeof d === 'string' ? d : d?.id)).filter(Boolean)
          : [];
      const newIds = Array.from(new Set([...existingIds, documentId]));

      const patchResp = await fetch(`https://api.elevenlabs.io/v1/convai/agents/${agentId}`, {
        method: 'PATCH',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ knowledge_base_ids: newIds })
      });
      if (!patchResp.ok) {
        const t = await patchResp.text().catch(() => '');
        throw new Error(`Failed to attach document: ${t || patchResp.statusText}`);
      }
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to attach document to agent');
    }
  };

  const uploadTextDocument = async () => {
    if (!formData.text || !formData.name) {
      setError('Name and text content are required');
      return;
    }

    setUploading(true);
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Create document in ElevenLabs
      const response = await fetch('https://api.elevenlabs.io/v1/convai/knowledge-base/text', {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          text: formData.text,
          prompt_injectable: formData.promptInjectable,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create text document: ${response.statusText}`);
      }

      const docData = await response.json();

      // Store tracking information in our database
      const { error: dbError } = await supabase
        .from('user_documents')
        .insert({
          user_id: user.id,
          elevenlabs_document_id: docData.id,
          name: formData.name,
          document_type: 'text'
        });

      if (dbError) {
        console.error('Error storing document tracking:', dbError);
        // Document was created in ElevenLabs but tracking failed
        // Still refresh to show it, but warn user
        setError('Document created but tracking failed. Please refresh if it doesn\'t appear.');
      }

      resetForm();
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create text document');
    } finally {
      setUploading(false);
    }
  };

  const uploadUrlDocument = async () => {
    if (!formData.url || !formData.name) {
      setError('Name and URL are required');
      return;
    }

    setUploading(true);
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Create document in ElevenLabs
      const response = await fetch('https://api.elevenlabs.io/v1/convai/knowledge-base/url', {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          url: formData.url,
          prompt_injectable: formData.promptInjectable,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create URL document: ${response.statusText}`);
      }

      const docData = await response.json();

      // Store tracking information in our database
      const { error: dbError } = await supabase
        .from('user_documents')
        .insert({
          user_id: user.id,
          elevenlabs_document_id: docData.id,
          name: formData.name,
          document_type: 'url'
        });

      if (dbError) {
        console.error('Error storing document tracking:', dbError);
        setError('Document created but tracking failed. Please refresh if it doesn\'t appear.');
      }

      resetForm();
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create URL document');
    } finally {
      setUploading(false);
    }
  };

  const uploadFileDocument = async () => {
    if (!formData.file || !formData.name) {
      setError('Name and file are required');
      return;
    }

    setUploading(true);
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Create document in ElevenLabs
      const formDataUpload = new FormData();
      formDataUpload.append('name', formData.name);
      formDataUpload.append('file', formData.file);
      formDataUpload.append('prompt_injectable', formData.promptInjectable.toString());

      const response = await fetch('https://api.elevenlabs.io/v1/convai/knowledge-base/file', {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
        },
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`);
      }

      const docData = await response.json();

      // Store tracking information in our database
      const { error: dbError } = await supabase
        .from('user_documents')
        .insert({
          user_id: user.id,
          elevenlabs_document_id: docData.id,
          name: formData.name,
          document_type: 'file'
        });

      if (dbError) {
        console.error('Error storing document tracking:', dbError);
        setError('Document created but tracking failed. Please refresh if it doesn\'t appear.');
      }

      resetForm();
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      // First verify the document belongs to the user
      const { data: userDoc, error: dbError } = await supabase
        .from('user_documents')
        .select('*')
        .eq('elevenlabs_document_id', documentId)
        .single();

      if (dbError || !userDoc) {
        throw new Error('Document not found or you do not have permission to delete it');
      }

      // Delete from ElevenLabs
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/knowledge-base/${documentId}`, {
        method: 'DELETE',
        headers: {
          'xi-api-key': apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.statusText}`);
      }

      // Remove tracking information from our database
      const { error: deleteDbError } = await supabase
        .from('user_documents')
        .delete()
        .eq('elevenlabs_document_id', documentId);

      if (deleteDbError) {
        console.error('Error removing document tracking:', deleteDbError);
        // Document was deleted from ElevenLabs but tracking removal failed
        setError('Document deleted but tracking cleanup failed. Please refresh.');
      }

      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    }
  };

  const computeRagIndex = async (documentId: string) => {
    try {
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/knowledge-base/${documentId}/rag-index`, {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'e5_mistral_7b_instruct',
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to compute RAG index: ${response.statusText}`);
      }

      alert('RAG index computation started successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to compute RAG index');
    }
  };

  const handleSubmit = () => {
    switch (uploadType) {
      case 'text':
        return uploadTextDocument();
      case 'url':
        return uploadUrlDocument();
      case 'file':
        return uploadFileDocument();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      text: '',
      url: '',
      file: null,
      promptInjectable: true,
    });
    setShowForm(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (unixTimestamp: number) => {
    return new Date(unixTimestamp * 1000).toLocaleDateString();
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'file':
        return '📄';
      case 'url':
        return '🔗';
      case 'text':
        return '📝';
      default:
        return '📚';
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchAgents();
  }, [fetchDocuments, fetchAgents]);

  if (!apiKey) {
    return (
      <div className="p-8 text-center">
        <div className="mb-6">
          <div className="text-6xl mb-4">🔑</div>
          <h3 className="text-xl font-semibold text-gray-300 mb-2">API Key Required</h3>
          <p className="text-gray-400">Please configure your API key in Settings to manage knowledge base.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Knowledge Base</h2>
          <p className="text-gray-400">Manage documents and content for your agents</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <span>+</span>
          <span>Add Document</span>
        </button>
      </div>

      {error && (
        <div className="glass-card rounded-2xl p-4 mb-6 border border-red-500/30 neon-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">❌</span>
              <p className="text-red-300">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">×</button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-300 text-lg">Loading documents...</p>
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-8xl mb-6">📚</div>
              <h3 className="text-2xl font-bold text-white mb-4">No documents found</h3>
              <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto">
                Add documents to provide context and knowledge to your agents. Enhance their intelligence with custom content.
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="btn-primary text-lg px-8 py-4"
              >
                Add Your First Document
              </button>
            </div>
          ) : (
            documents.map((document) => (
              <div key={document.id} className="rounded-2xl border border-white/10 bg-gray-800/60 hover:bg-gray-800/80 transition-colors p-5">
                <div className="flex items-start justify-between">
                  <div className="pr-2">
                    <h3 className="text-lg font-semibold text-white mb-1">{document.name}</h3>
                    <div className="text-xs text-gray-400 flex items-center space-x-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300">
                        {document.type.toUpperCase()}
                      </span>
                      {document.prompt_injectable && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-500/20 border border-green-500/30 text-green-300">
                          Injectable
                        </span>
                      )}
                      {document.metadata && (
                        <span>
                          {formatFileSize(document.metadata.size_bytes)} • {formatDate(document.metadata.created_at_unix_secs)}
                        </span>
                      )}
                    </div>
                    {document.dependent_agents && document.dependent_agents.length > 0 && (
                      <div className="mt-2 text-xs text-blue-300">Used by {document.dependent_agents.length} agent(s)</div>
                    )}
                  </div>
                  <div className="text-2xl">{getDocumentIcon(document.type)}</div>
                </div>

                <div className="mt-4 flex items-center space-x-2">
                  <select
                    value={docToAgentSelection[document.id] || ''}
                    onChange={(e) => setDocToAgentSelection(prev => ({ ...prev, [document.id]: e.target.value }))}
                    className="flex-1 bg-black/20 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Attach to agent...</option>
                    {agents.map(a => (
                      <option key={a.agent_id} value={a.agent_id}>{a.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => attachDocumentToAgent(document.id)}
                    className="btn-primary px-4 py-2"
                  >
                    Attach
                  </button>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <button onClick={() => computeRagIndex(document.id)} className="text-blue-300 hover:text-blue-200 text-sm">⚡ Compute RAG</button>
                  <button onClick={() => deleteDocument(document.id)} className="text-red-300 hover:text-red-200 text-sm">Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add Document Form Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="glass-card rounded-2xl max-w-2xl w-full max-h-[calc(100vh-4rem)] overflow-y-auto scrollbar-thin border border-white/10">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    <span>Add Document</span>
                  </h2>
                  <p className="text-gray-400">Upload content to enhance your agents' knowledge</p>
                </div>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-white text-2xl transition-colors hover:bg-white/10 rounded-lg p-2"
                >
                  ×
                </button>
              </div>

              {/* Upload Type Selection */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-300 mb-4">
                  Document Type
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: 'text', label: 'Text', icon: '📝', desc: 'Raw text content' },
                    { value: 'url', label: 'URL', icon: '🔗', desc: 'Web content' },
                    { value: 'file', label: 'File', icon: '📄', desc: 'Document upload' },
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setUploadType(type.value as 'file' | 'url' | 'text')}
                      className={`p-6 rounded-xl text-center transition-all duration-300 ${
                        uploadType === type.value
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'glass-effect border border-white/10 text-gray-300 hover:border-white/20'
                      }`}
                    >
                      <div className="text-3xl mb-3">{type.icon}</div>
                      <div className="font-semibold mb-1">{type.label}</div>
                      <div className="text-xs opacity-75">{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Document Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter document name"
                  />
                </div>

                {uploadType === 'text' && (
                  <div>
                    <label htmlFor="text" className="block text-sm font-medium text-gray-300 mb-2">
                      Text Content *
                    </label>
                    <textarea
                      id="text"
                      value={formData.text}
                      onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                      required
                      rows={8}
                      className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Enter text content for the knowledge base"
                    />
                  </div>
                )}

                {uploadType === 'url' && (
                  <div>
                    <label htmlFor="url" className="block text-sm font-medium text-gray-300 mb-2">
                      URL *
                    </label>
                    <input
                      type="url"
                      id="url"
                      value={formData.url}
                      onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                      required
                      className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/document"
                    />
                  </div>
                )}

                {uploadType === 'file' && (
                  <div>
                    <label htmlFor="file" className="block text-sm font-medium text-gray-300 mb-2">
                      File *
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        id="file"
                        onChange={(e) => setFormData(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                        required
                        className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-500/20 file:text-blue-300"
                        accept=".pdf,.txt,.docx,.md"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Supported formats: PDF, TXT, DOCX, MD
                    </p>
                  </div>
                )}

                <div className="glass-effect rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="promptInjectable"
                      checked={formData.promptInjectable}
                      onChange={(e) => setFormData(prev => ({ ...prev, promptInjectable: e.target.checked }))}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded bg-black/20"
                    />
                    <div>
                      <label htmlFor="promptInjectable" className="block text-white font-medium">
                        Make this document prompt injectable
                      </label>
                      <p className="text-sm text-gray-400">Allow agents to reference this content in responses</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-8 border-t border-white/10 mt-8">
                <button
                  onClick={resetForm}
                  className="btn-secondary px-6 py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={uploading}
                  className="btn-primary px-6 py-3 disabled:opacity-50"
                >
                  {uploading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Adding...</span>
                    </div>
                  ) : (
                    '📚 Add Document'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default KnowledgeBase; 